import { useCallback, useEffect, useRef, useState } from 'react';
import type { EinweisungPublic } from '../../sige-plan/types';
import {
  einweisungFehlerCode,
  ladeEinweisungPublic,
  schliesseOnlineTeilnahmeAb,
} from '../../../services/sigeEinweisungPublicService';
import { describeError } from '../../../services/errorMessage';
import {
  LEERER_ZUSTAND,
  fehlerTextZuCode,
  formularBereinigt,
  naechstePerson as naechstePersonFormular,
  naechsterSchritt,
  parseZustand,
  serialisiereZustand,
  speicherSchluessel,
  vollerName,
  vorherigerSchritt,
  type TeilnahmeFormular,
  type TeilnahmeZustand,
} from './einweisungTeilnahmeLogic';

/**
 * Zustand und Handlungen der oeffentlichen Einweisungsseite.
 *
 * Schritt und Formular liegen im sessionStorage je linkToken
 * (`osia.einweisung.online.<token>`), damit ein Neuladen nicht alles
 * loescht; die Einweisung selbst wird bei jedem Laden frisch ueber die
 * anon-RPC geholt, damit ein zwischenzeitlich deaktivierter Link auch
 * wirklich zu ist. Die Unterschrift wird NIE gespeichert — sie geht direkt
 * an die RPC.
 *
 * Kein Login, keine Tabelle: nur `sigeEinweisungPublicService`.
 */

export type LadeStatus = 'laedt' | 'bereit' | 'ungueltig' | 'fehler';

const leseGespeichert = (linkToken: string): TeilnahmeZustand => {
  try {
    return parseZustand(sessionStorage.getItem(speicherSchluessel(linkToken))) ?? LEERER_ZUSTAND;
  } catch {
    // Privater Modus / gesperrter Speicher: die Seite laeuft ohne Persistenz weiter.
    return LEERER_ZUSTAND;
  }
};

const speichere = (linkToken: string, zustand: TeilnahmeZustand): void => {
  try {
    sessionStorage.setItem(speicherSchluessel(linkToken), serialisiereZustand(zustand));
  } catch {
    // s. o. — ein voller oder gesperrter Speicher darf die Teilnahme nicht stoppen.
  }
};

const userAgent = (): string | undefined => {
  try {
    const ua = navigator.userAgent;
    return ua ? ua.slice(0, 500) : undefined;
  } catch {
    return undefined;
  }
};

export const useEinweisungTeilnahme = (linkToken: string) => {
  const [ladeStatus, setLadeStatus] = useState<LadeStatus>('laedt');
  const [ladeFehler, setLadeFehler] = useState('');
  const [einweisung, setEinweisung] = useState<EinweisungPublic | null>(null);
  const [zustand, setZustand] = useState<TeilnahmeZustand>(() => leseGespeichert(linkToken));
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  /**
   * Riegel gegen den zweiten Fingertipp auf „Übernehmen": `busy` steht erst
   * nach dem naechsten Render, zwei schnelle Beruehrungen liefen davor beide
   * durch und schickten dieselbe Person zweimal an die RPC.
   */
  const laeuft = useRef(false);

  useEffect(() => {
    let aktiv = true;
    setLadeStatus('laedt');
    ladeEinweisungPublic(linkToken)
      .then((e) => {
        if (!aktiv) return;
        if (!e) {
          setLadeStatus('ungueltig');
          return;
        }
        setEinweisung(e);
        setLadeStatus('bereit');
      })
      .catch((err) => {
        if (!aktiv) return;
        setLadeFehler(describeError(err));
        setLadeStatus('fehler');
      });
    return () => {
      aktiv = false;
    };
  }, [linkToken]);

  useEffect(() => {
    speichere(linkToken, zustand);
  }, [linkToken, zustand]);

  const weiter = useCallback(() => {
    setZustand((z) => ({ ...z, schritt: naechsterSchritt(z.schritt) }));
    setFehler(null);
  }, []);

  const zurueck = useCallback(() => {
    setZustand((z) => ({ ...z, schritt: vorherigerSchritt(z.schritt) }));
    setFehler(null);
  }, []);

  const formularAendern = useCallback(<K extends keyof TeilnahmeFormular>(feld: K, wert: TeilnahmeFormular[K]) => {
    setZustand((z) => ({ ...z, formular: { ...z.formular, [feld]: wert } }));
  }, []);

  /**
   * Abschluss mit Unterschrift: eine RPC, Rueckgabe { bereits, unterschriebenAm }.
   * P0002 heisst: der Link ist inzwischen zu (deaktiviert, abgelaufen) — dann
   * zeigt die Seite „nicht mehr gültig" statt eines Fehlerkastens.
   */
  const abschliessen = useCallback(
    async (unterschrift: string) => {
      if (laeuft.current) return;
      laeuft.current = true;
      setBusy(true);
      setFehler(null);
      try {
        const f = formularBereinigt(zustand.formular);
        const antwort = await schliesseOnlineTeilnahmeAb(linkToken, {
          vorname: f.vorname,
          nachname: f.nachname,
          funktion: f.funktion,
          firma: f.firma,
          unterschrift,
          userAgent: userAgent(),
        });
        setZustand((z) => ({
          ...z,
          schritt: 'ergebnis',
          ergebnis: { bereits: antwort.bereits, unterschriebenAm: antwort.unterschriebenAm, name: vollerName(f) },
        }));
      } catch (e) {
        const code = einweisungFehlerCode(e);
        if (code === 'P0002') {
          setLadeStatus('ungueltig');
        } else {
          setFehler(fehlerTextZuCode(code, describeError(e)));
        }
      } finally {
        laeuft.current = false;
        setBusy(false);
      }
    },
    [linkToken, zustand.formular],
  );

  /**
   * Das Handy wird weitergereicht: Name und Haekchen leer, Firma und Funktion
   * bleiben — und die naechste Person liest den Inhalt selbst (Schritt
   * „inhalt"), denn die Unterschrift gilt fuer das, was gezeigt wurde.
   */
  const naechstePerson = useCallback(() => {
    setFehler(null);
    setZustand((z) => ({
      ...z,
      schritt: 'inhalt',
      formular: naechstePersonFormular(z.formular),
      ergebnis: null,
    }));
  }, []);

  return {
    ladeStatus,
    ladeFehler,
    einweisung,
    zustand,
    schritt: zustand.schritt,
    ergebnis: zustand.ergebnis,
    busy,
    fehler,
    weiter,
    zurueck,
    formularAendern,
    abschliessen,
    naechstePerson,
  };
};

export type EinweisungTeilnahmeSteuerung = ReturnType<typeof useEinweisungTeilnahme>;
